/**
 * 来源URL跟踪工具
 */

const SOURCE_URL_KEY = 'source_url';
const ENTRY_TIME_KEY = 'entry_time';
const SESSION_ID_KEY = 'session_id';
const CACHE_VERSION_KEY = 'cache_version';

// 缓存配置
const CACHE_CONFIG = {
  // 缓存过期时间（毫秒）- 默认2分钟
  EXPIRY_TIME: 2 * 60 * 1000,
  // 会话过期时间（毫秒）- 默认4小时  
  SESSION_EXPIRY: 4 * 60 * 60 * 1000,
  // 缓存版本，用于强制更新
  VERSION: '1.0.0'
};

export class SourceTracker {
  /**
   * 生成会话ID
   */
  private static generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * 获取当前会话ID
   */
  private static getSessionId(): string {
    let sessionId = localStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = this.generateSessionId();
      localStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  /**
   * 检查缓存是否有效
   */
  private static isCacheValid(): boolean {
    // 检查版本
    const cacheVersion = localStorage.getItem(CACHE_VERSION_KEY);
    if (cacheVersion !== CACHE_CONFIG.VERSION) {
      console.log('缓存版本不匹配，清除旧缓存');
      this.clearSourceUrl();
      return false;
    }

    // 检查是否有缓存数据
    const sourceUrl = localStorage.getItem(SOURCE_URL_KEY);
    const entryTime = localStorage.getItem(ENTRY_TIME_KEY);
    
    if (!sourceUrl || !entryTime) {
      return false;
    }

    // 检查缓存是否过期
    const entryTimestamp = new Date(entryTime).getTime();
    const now = Date.now();
    const timeDiff = now - entryTimestamp;

    if (timeDiff > CACHE_CONFIG.EXPIRY_TIME) {
      console.log('缓存已过期，清除缓存');
      this.clearSourceUrl();
      return false;
    }

    return true;
  }

  /**
   * 检查会话是否有效
   */
  private static isSessionValid(): boolean {
    const entryTime = localStorage.getItem(ENTRY_TIME_KEY);
    if (!entryTime) return false;

    const entryTimestamp = new Date(entryTime).getTime();
    const now = Date.now();
    const timeDiff = now - entryTimestamp;

    return timeDiff <= CACHE_CONFIG.SESSION_EXPIRY;
  }

  /**
   * 强制更新来源URL（忽略缓存）
   */
  static forceUpdateSourceUrl(): void {
    this.clearSourceUrl();
    this.recordSourceUrl();
  }

  /**
   * 记录来源URL（在用户首次进入网站时调用）
   */
  static recordSourceUrl(): void {
    // 检查缓存是否有效，如果有效则不重复记录
    if (this.isCacheValid()) {
      console.log('使用有效缓存，跳过记录');
      return;
    }

    // 获取来源URL（从哪个页面跳转过来的）
    const referrerUrl = document.referrer;
    // 获取当前页面URL（用户访问的URL，可能包含参数）
    const currentUrl = window.location.href;
    
    // 智能判断来源URL
    let sourceUrl = '';
    
    // 1. 如果有referrer且不是本站内跳转，使用referrer
    if (referrerUrl && !this.isSameDomain(referrerUrl, currentUrl)) {
      sourceUrl = referrerUrl;
    }
    // 2. 检查URL参数中是否有来源信息
    else if (this.hasUrlSourceParams(currentUrl)) {
      sourceUrl = this.extractSourceFromUrl(currentUrl);
    }
    // 3. 如果referrer是搜索引擎域名，优先使用referrer
    else if (referrerUrl && this.isSearchEngineDomain(referrerUrl)) {
      sourceUrl = referrerUrl;
    }
    // 4. 最后才使用当前URL
    else {
      sourceUrl = referrerUrl || currentUrl;
    }
    
    const entryTime = new Date().toISOString();
    const sessionId = this.getSessionId();
    
    // 存储来源信息和元数据
    localStorage.setItem(SOURCE_URL_KEY, sourceUrl);
    localStorage.setItem(ENTRY_TIME_KEY, entryTime);
    localStorage.setItem(SESSION_ID_KEY, sessionId);
    localStorage.setItem(CACHE_VERSION_KEY, CACHE_CONFIG.VERSION);
    
    console.log('记录来源URL:', sourceUrl);
    console.log('Referrer URL:', referrerUrl);
    console.log('当前页面URL:', currentUrl);
    console.log('是否搜索引擎域名:', this.isSearchEngineDomain(referrerUrl));
    console.log('是否百度域名:', this.isBaiduDomain(referrerUrl));
    console.log('是否同域名:', this.isSameDomain(referrerUrl, currentUrl));
  }

  /**
   * 获取记录的来源URL
   */
  static getSourceUrl(): string {
    return localStorage.getItem(SOURCE_URL_KEY) || '';
  }

  /**
   * 获取进入时间
   */
  static getEntryTime(): string {
    return localStorage.getItem(ENTRY_TIME_KEY) || '';
  }

  /**
   * 清除记录的来源URL（可选，用于测试或重置）
   */
  static clearSourceUrl(): void {
    localStorage.removeItem(SOURCE_URL_KEY);
    localStorage.removeItem(ENTRY_TIME_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
    localStorage.removeItem(CACHE_VERSION_KEY);
  }

  /**
   * 检查是否有特定参数
   */
  static hasParameter(paramName: string): boolean {
    const sourceUrl = this.getSourceUrl();
    if (!sourceUrl) return false;
    
    try {
      const url = new URL(sourceUrl);
      return url.searchParams.has(paramName);
    } catch (error) {
      console.error('解析来源URL失败:', error);
      return false;
    }
  }

  /**
   * 获取来源URL中的特定参数值
   */
  static getParameter(paramName: string): string | null {
    const sourceUrl = this.getSourceUrl();
    if (!sourceUrl) return null;
    
    try {
      const url = new URL(sourceUrl);
      return url.searchParams.get(paramName);
    } catch (error) {
      console.error('解析来源URL失败:', error);
      return null;
    }
  }

  /**
   * 获取用于下单接口的来源URL参数
   * 直接返回记录的来源URL，如果没有则返回空字符串
   */
  static getSourceUrlForOrder(): string {
    return this.getSourceUrl();
  }

  /**
   * 判断两个URL是否为同一域名
   */
  private static isSameDomain(url1: string, url2: string): boolean {
    if (!url1 || !url2) return false;
    try {
      const domain1 = new URL(url1).hostname;
      const domain2 = new URL(url2).hostname;
      return domain1 === domain2;
    } catch (error) {
      return false;
    }
  }

  /**
   * 判断是否为百度相关域名
   */
  private static isBaiduDomain(url: string): boolean {
    if (!url) return false;
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      return hostname.includes('baidu.com') || 
             hostname.includes('baidu.cn') ||
             hostname.includes('baidu') ||
             hostname === 'm.baidu.com' ||
             hostname === 'www.baidu.com';
    } catch (error) {
      return false;
    }
  }

  /**
   * 判断是否为搜索引擎域名
   */
  private static isSearchEngineDomain(url: string): boolean {
    if (!url) return false;
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      
      // 百度系列
      if (hostname.includes('baidu.com') || hostname.includes('baidu.cn') || hostname.includes('baidu')) {
        return true;
      }
      
      // Google系列
      if (hostname.includes('google.com') || hostname.includes('google.cn') || 
          hostname.includes('google.co') || hostname.includes('google.')) {
        return true;
      }
      
      // Bing系列
      if (hostname.includes('bing.com') || hostname.includes('bing.cn') || 
          hostname === 'cn.bing.com' || hostname === 'www.bing.com') {
        return true;
      }
      
      // 搜狗系列
      if (hostname.includes('sogou.com') || hostname.includes('sogo.com')) {
        return true;
      }
      
      // 360搜索系列
      if (hostname.includes('so.com') || hostname.includes('360.cn') || 
          hostname === 'www.so.com' || hostname === 'so.360.cn') {
        return true;
      }
      
      // 神马搜索
      if (hostname.includes('sm.cn') || hostname.includes('uc.cn')) {
        return true;
      }
      
      // 头条搜索
      if (hostname.includes('toutiao.com') && hostname.includes('search')) {
        return true;
      }
      
      // 夸克搜索
      if (hostname.includes('quark.cn') || hostname.includes('quark.com')) {
        return true;
      }
      
      // 其他搜索引擎
      if (hostname.includes('yahoo.com') || hostname.includes('duckduckgo.com') || 
          hostname.includes('yandex.com') || hostname.includes('ask.com')) {
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * 检查URL中是否包含来源参数
   */
  private static hasUrlSourceParams(url: string): boolean {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      // 检查常见的来源参数
      return urlObj.searchParams.has('utm_source') ||
             urlObj.searchParams.has('source') ||
             urlObj.searchParams.has('from') ||
             urlObj.searchParams.has('ref') ||
             urlObj.searchParams.has('referrer');
    } catch (error) {
      return false;
    }
  }

  /**
   * 从URL参数中提取来源信息
   */
  private static extractSourceFromUrl(url: string): string {
    if (!url) return url;
    try {
      const urlObj = new URL(url);
      // 按优先级检查各种来源参数
      const sourceParams = ['utm_source', 'source', 'from', 'ref', 'referrer'];
      
      for (const param of sourceParams) {
        const value = urlObj.searchParams.get(param);
        if (value) {
          return `${param}=${value}`;
        }
      }
      
      return url; // 如果没有找到特定参数，返回完整URL
    } catch (error) {
      return url;
    }
  }

  /**
   * 获取百度搜索关键词（如果来源是百度搜索）
   */
  static getBaiduSearchKeyword(): string | null {
    const sourceUrl = this.getSourceUrl();
    if (!sourceUrl || !this.isBaiduDomain(sourceUrl)) {
      return null;
    }
    
    try {
      const url = new URL(sourceUrl);
      // 百度搜索的关键词参数通常是 'wd' 或 'word'
      return url.searchParams.get('wd') || url.searchParams.get('word') || null;
    } catch (error) {
      console.error('解析百度搜索关键词失败:', error);
      return null;
    }
  }

  /**
   * 获取搜索关键词（支持所有搜索引擎）
   */
  static getSearchKeyword(): string | null {
    const sourceUrl = this.getSourceUrl();
    if (!sourceUrl || !this.isSearchEngineDomain(sourceUrl)) {
      return null;
    }
    
    try {
      const url = new URL(sourceUrl);
      const hostname = url.hostname.toLowerCase();
      
      // 百度搜索关键词参数
      if (hostname.includes('baidu')) {
        return url.searchParams.get('wd') || url.searchParams.get('word') || null;
      }
      
      // Google搜索关键词参数
      if (hostname.includes('google')) {
        return url.searchParams.get('q') || url.searchParams.get('query') || null;
      }
      
      // Bing搜索关键词参数
      if (hostname.includes('bing')) {
        return url.searchParams.get('q') || url.searchParams.get('query') || null;
      }
      
      // 搜狗搜索关键词参数
      if (hostname.includes('sogou') || hostname.includes('sogo')) {
        return url.searchParams.get('query') || url.searchParams.get('q') || null;
      }
      
      // 360搜索关键词参数
      if (hostname.includes('so.com') || hostname.includes('360')) {
        return url.searchParams.get('q') || url.searchParams.get('query') || null;
      }
      
      // 神马搜索关键词参数
      if (hostname.includes('sm.cn') || hostname.includes('uc.cn')) {
        return url.searchParams.get('q') || url.searchParams.get('query') || null;
      }
      
      // 头条搜索关键词参数
      if (hostname.includes('toutiao.com')) {
        return url.searchParams.get('keyword') || url.searchParams.get('q') || null;
      }
      
      // 夸克搜索关键词参数
      if (hostname.includes('quark')) {
        return url.searchParams.get('q') || url.searchParams.get('query') || null;
      }
      
      // Yahoo搜索关键词参数
      if (hostname.includes('yahoo')) {
        return url.searchParams.get('p') || url.searchParams.get('q') || null;
      }
      
      // DuckDuckGo搜索关键词参数
      if (hostname.includes('duckduckgo')) {
        return url.searchParams.get('q') || url.searchParams.get('query') || null;
      }
      
      // Yandex搜索关键词参数
      if (hostname.includes('yandex')) {
        return url.searchParams.get('text') || url.searchParams.get('q') || null;
      }
      
      // 通用关键词参数作为后备
      return url.searchParams.get('q') || 
             url.searchParams.get('query') || 
             url.searchParams.get('wd') || 
             url.searchParams.get('keyword') || 
             null;
             
    } catch (error) {
      console.error('解析搜索关键词失败:', error);
      return null;
    }
  }

  /**
   * 获取来源类型（搜索引擎、直接访问、外链等）
   */
  static getSourceType(): string {
    const sourceUrl = this.getSourceUrl();
    if (!sourceUrl) return 'unknown';
    
    try {
      const url = new URL(sourceUrl);
      const hostname = url.hostname.toLowerCase();
      
      // 判断搜索引擎
      if (hostname.includes('baidu')) return 'baidu';
      if (hostname.includes('google')) return 'google';
      if (hostname.includes('bing')) return 'bing';
      if (hostname.includes('sogou') || hostname.includes('sogo')) return 'sogou';
      if (hostname.includes('so.com') || hostname.includes('360')) return '360search';
      if (hostname.includes('sm.cn') || hostname.includes('uc.cn')) return 'shenma';
      if (hostname.includes('toutiao.com')) return 'toutiao';
      if (hostname.includes('quark')) return 'quark';
      if (hostname.includes('yahoo')) return 'yahoo';
      if (hostname.includes('duckduckgo')) return 'duckduckgo';
      if (hostname.includes('yandex')) return 'yandex';
      if (hostname.includes('ask.com')) return 'ask';
      
      // 判断社交媒体
      if (hostname.includes('weixin') || hostname.includes('qq.com')) return 'wechat';
      if (hostname.includes('weibo')) return 'weibo';
      if (hostname.includes('zhihu')) return 'zhihu';
      if (hostname.includes('douyin') || hostname.includes('tiktok')) return 'douyin';
      if (hostname.includes('xiaohongshu') || hostname.includes('xhs')) return 'xiaohongshu';
      
      // 判断其他平台
      if (hostname.includes('bilibili')) return 'bilibili';
      if (hostname.includes('csdn')) return 'csdn';
      if (hostname.includes('jianshu')) return 'jianshu';
      if (hostname.includes('github')) return 'github';
      
      // 判断是否为当前网站
      if (this.isSameDomain(sourceUrl, window.location.href)) return 'internal';
      
      return 'external';
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * 获取详细的来源信息（包括搜索关键词）
   */
  static getSourceInfo(): {
    url: string;
    type: string;
    keyword: string | null;
    entryTime: string;
    sessionId: string;
    cacheValid: boolean;
    sessionValid: boolean;
  } {
    return {
      url: this.getSourceUrl(),
      type: this.getSourceType(),
      keyword: this.getSearchKeyword(),
      entryTime: this.getEntryTime(),
      sessionId: this.getSessionId(),
      cacheValid: this.isCacheValid(),
      sessionValid: this.isSessionValid()
    };
  }

  /**
   * 获取缓存状态信息
   */
  static getCacheStatus(): {
    hasCache: boolean;
    isValid: boolean;
    isExpired: boolean;
    remainingTime: number;
    sessionId: string;
    version: string;
  } {
    const sourceUrl = localStorage.getItem(SOURCE_URL_KEY);
    const entryTime = localStorage.getItem(ENTRY_TIME_KEY);
    const sessionId = localStorage.getItem(SESSION_ID_KEY) || '';
    const version = localStorage.getItem(CACHE_VERSION_KEY) || '';
    
    const hasCache = !!(sourceUrl && entryTime);
    let isValid = false;
    let isExpired = false;
    let remainingTime = 0;

    if (hasCache) {
      const entryTimestamp = new Date(entryTime!).getTime();
      const now = Date.now();
      const timeDiff = now - entryTimestamp;
      
      isExpired = timeDiff > CACHE_CONFIG.EXPIRY_TIME;
      isValid = !isExpired && version === CACHE_CONFIG.VERSION;
      remainingTime = Math.max(0, CACHE_CONFIG.EXPIRY_TIME - timeDiff);
    }

    return {
      hasCache,
      isValid,
      isExpired,
      remainingTime,
      sessionId,
      version
    };
  }

  /**
   * 设置缓存过期时间（分钟）
   */
  static setCacheExpiryTime(minutes: number): void {
    CACHE_CONFIG.EXPIRY_TIME = minutes * 60 * 1000;
    console.log(`缓存过期时间已设置为 ${minutes} 分钟`);
  }

  /**
   * 设置会话过期时间（小时）
   */
  static setSessionExpiryTime(hours: number): void {
    CACHE_CONFIG.SESSION_EXPIRY = hours * 60 * 60 * 1000;
    console.log(`会话过期时间已设置为 ${hours} 小时`);
  }

  /**
   * 刷新缓存（延长过期时间）
   */
  static refreshCache(): boolean {
    const sourceUrl = localStorage.getItem(SOURCE_URL_KEY);
    if (!sourceUrl) {
      console.log('无缓存数据，无法刷新');
      return false;
    }

    const newEntryTime = new Date().toISOString();
    localStorage.setItem(ENTRY_TIME_KEY, newEntryTime);
    console.log('缓存已刷新，新的过期时间:', new Date(Date.now() + CACHE_CONFIG.EXPIRY_TIME));
    return true;
  }

  /**
   * 检查并清理过期缓存
   */
  static cleanupExpiredCache(): void {
    const status = this.getCacheStatus();
    if (status.hasCache && status.isExpired) {
      console.log('清理过期缓存');
      this.clearSourceUrl();
    }
  }
}
